'use client';

import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@ui/icons';
import { Button, IconButton } from '@ui/button';
import { Eyebrow } from '@web/atoms';
import { IMAGE_SIZES, ResponsiveImage } from '@web/molecules';
import { useReporter } from '../../reporter';
import {
  userViewsOnboardingIntro,
  userContinuesFromIntro,
  onboardingBackArrowClicked,
} from '../events';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import { DropoffPath } from './DropoffPath';
import {
  StyledOnboardingIntro,
  MobileImageContainer,
  DesktopImageContainer,
  ImageContainer,
  Headline,
  ButtonContainer,
  BodyWrapper,
  Body,
  MobileHeader,
  DesktopHeader,
  LogoLink,
  PopUpBar,
} from './styles.js';

export const OnboardingIntro = ({
  goBack,
  onContinue,
  mobileImage,
  desktopImage,
  headline,
  ctaButtonText = 'Start the quiz',
  body,
  ollieLogo,
  mobileLogo,
  fontColor = 'text.primary',
  popUpText,
  popUpFontColor = 'text.lightest',
  popUpBgColor = 'status.successPrimary',
  popUpAlignment = 'start',
  eyebrow,
  typography = 'heading2',
  bodyTypography = 'body',
  dropoffPath,
  logoUrl = '/',
  flowType = '/onboarding',
}) => {
  const [showPopup, setShowPopup] = useState(true);
  const { cart } = useShopifyCartContext();

  const reporter = useReporter();
  const viewPageTagFired = useRef(false);
  useEffect(() => {
    if (!viewPageTagFired.current) {
      reporter.tag(userViewsOnboardingIntro());
      viewPageTagFired.current = true;
    }
  }, [reporter]);

  useEffect(() => {
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 5000);
  }, []);
  const handleSubmit = () => {
    reporter.tag(userContinuesFromIntro());
    onContinue();
  };

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('onboarding_intro', cart?.customer),
    );
    goBack();
  };

  return (
    <>
      <StyledOnboardingIntro>
        <MobileHeader>
          <LogoLink href={logoUrl} aria-label="Ollie home">
            <ResponsiveImage priority={true} {...(mobileLogo ?? ollieLogo)} />
          </LogoLink>
        </MobileHeader>
        <ButtonContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            data-testid="onboarding-intro-back"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.dark',
            }}
            onClick={goBackWithReporter}
            type="button"
            variant="secondary"
          />
        </ButtonContainer>
        <ImageContainer>
          <MobileImageContainer>
            <ResponsiveImage
              priority={true}
              {...mobileImage}
              width={400}
              sizes={IMAGE_SIZES.halfAtLaptop}
            />
          </MobileImageContainer>
          <DesktopImageContainer>
            <ResponsiveImage
              priority={true}
              {...desktopImage}
              height={362}
              width={406}
              sizes={IMAGE_SIZES.halfAtLaptop}
            />
          </DesktopImageContainer>
        </ImageContainer>
        <BodyWrapper $minHeight="0">
          {showPopup && popUpText && (
            <PopUpBar
              $fontColor={popUpFontColor}
              $bgColor={popUpBgColor}
              $alignment={popUpAlignment}
              showPopup={showPopup}
            >
              <Icon
                iconName="checkmark"
                alt="checkmark"
                color={popUpFontColor}
                size="15"
                id={`check`}
              />
              {popUpText}
            </PopUpBar>
          )}
          <DesktopHeader>
            <LogoLink href={logoUrl} aria-label="Ollie home">
              <ResponsiveImage priority={true} {...ollieLogo} />
            </LogoLink>
          </DesktopHeader>
          {eyebrow && (
            <Eyebrow
              textAlign={'center'}
              marginBottom={0}
              fontColor={'hero.plum'}
            >
              {eyebrow}
            </Eyebrow>
          )}
          {headline && (
            <Headline $fontColor={fontColor} $typography={typography}>
              {headline}
            </Headline>
          )}
          {body && (
            <Body $fontColor={fontColor} $typography={bodyTypography}>
              {body}
            </Body>
          )}
          <Button
            type="submit"
            aria-label={ctaButtonText}
            data-testid="onboarding-start-quiz"
            variant="primary"
            horizontalPadding={32}
            onClick={handleSubmit}
          >
            {ctaButtonText}
          </Button>
          {dropoffPath && <DropoffPath {...dropoffPath} flowType={flowType} />}
        </BodyWrapper>
      </StyledOnboardingIntro>
    </>
  );
};
OnboardingIntro.displayName = 'OnboardingIntro';
OnboardingIntro.propTypes = {
  ctaButtonText: PropTypes.string,
  mobileLogo: PropTypes.object,
  mobileImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
  headline: PropTypes.string,
  goBack: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  body: PropTypes.string,
  ollieLogo: PropTypes.object,
  popUpText: PropTypes.string,
  popUpBgColor: PropTypes.string,
  popUpFontColor: PropTypes.string,
  popUpAlignment: PropTypes.oneOf(['start', 'center', 'end']),
  eyebrow: PropTypes.string,
  typography: PropTypes.string,
  bodyTypography: PropTypes.string,
  logoUrl: PropTypes.string,
  flowType: PropTypes.string,
};
